import { useEffect, useState, useCallback, useRef } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DomainManager, DocumentEditor, TestRetrieval, GapLog, titleize } from "./KnowledgeAdmin";
import KnowledgeMetadataReview from "./KnowledgeMetadataReview";

const SOURCE_META = {
  website: { label: "Website", icon: "Globe", tone: "text-sky-600 bg-sky-50" },
  pdf: { label: "PDF", icon: "FileText", tone: "text-rose-600 bg-rose-50" },
  word: { label: "Word", icon: "FileType", tone: "text-blue-600 bg-blue-50" },
  spreadsheet: { label: "Spreadsheet", icon: "Sheet", tone: "text-emerald-600 bg-emerald-50" },
  qa: { label: "Q&A", icon: "MessageCircleQuestion", tone: "text-violet-600 bg-violet-50" },
  chat: { label: "Past Chat", icon: "MessagesSquare", tone: "text-amber-600 bg-amber-50" },
};
const inp = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500";
const lbl = "text-xs font-medium text-slate-500 mb-1 block";

export default function KnowledgeCenter() {
  const [collections, setCollections] = useState(null);
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState({});
  const [domains, setDomains] = useState([]);
  const [taxonomy, setTaxonomy] = useState(null);
  const [active, setActive] = useState(null); // collection id
  const [showNew, setShowNew] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showDomains, setShowDomains] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [showGaps, setShowGaps] = useState(false);
  const [showEnrich, setShowEnrich] = useState(false);
  const [enrichColId, setEnrichColId] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [search, setSearch] = useState("");
  const [domainFilter, setDomainFilter] = useState("all");
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggleGroup = (id) => setCollapsed((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const loadMeta = useCallback(() => {
    api.get("/knowledge/meta").then((r) => {
      setAgents(r.data.agents || []); setStats(r.data.stats || {});
      setDomains(r.data.domains || []); setTaxonomy(r.data.taxonomy || null);
    }).catch(() => {});
  }, []);
  const load = useCallback(() => {
    api.get("/knowledge/collections", { params: { include_archived: true } })
      .then((r) => setCollections(r.data.collections || [])).catch(() => setCollections([]));
  }, []);
  useEffect(() => { loadMeta(); load(); }, [loadMeta, load]);
  const loadPending = useCallback(() => {
    api.get("/knowledge/metadata-proposals", { params: { status: "pending" } })
      .then((r) => setPendingCount((r.data.proposals || []).length)).catch(() => setPendingCount(0));
  }, []);
  useEffect(() => { loadPending(); }, [loadPending]);
  const pendingLabel = pendingCount >= 500 ? "500+" : String(pendingCount);
  const openEnrich = (cid = null) => { setEnrichColId(cid); setShowEnrich(true); };
  const colOpts = (collections || []).filter((c) => c.status !== "archived").map((c) => ({ id: c.id, name: c.name }));

  return (
    <div className="p-6 max-w-[1280px] mx-auto space-y-5" data-testid="knowledge-center-page">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-indigo-600 flex items-center justify-center"><Icons.Library className="h-5 w-5 text-white" /></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">AI Knowledge</h1>
            <p className="text-sm text-slate-500">Reusable Knowledge Collections granted to AI agents. {stats.collections ?? 0} collections · {stats.items ?? 0} items.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowGaps(true)} data-testid="kc-gaps-btn" className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 text-sm rounded-lg px-3.5 py-2.5 hover:border-indigo-400"><Icons.SearchX className="h-4 w-4 text-indigo-500" />Knowledge Gaps</button>
          <button onClick={() => openEnrich(null)} data-testid="kc-enrich-btn" className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 text-sm rounded-lg px-3.5 py-2.5 hover:border-indigo-400"><Icons.Sparkles className="h-4 w-4 text-indigo-500" />AI Enrichment{pendingCount > 0 && <span data-testid="kc-enrich-badge" className="ml-0.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-indigo-600 text-white text-[11px] font-semibold">{pendingLabel}</span>}</button>
          <button onClick={() => setShowTest(true)} data-testid="kc-test-retrieval-btn" className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 text-sm rounded-lg px-3.5 py-2.5 hover:border-indigo-400"><Icons.FlaskConical className="h-4 w-4 text-indigo-500" />Test Retrieval</button>
          <button onClick={() => setShowDomains(true)} data-testid="kc-domains-btn" className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 text-sm rounded-lg px-3.5 py-2.5 hover:border-indigo-400"><Icons.FolderTree className="h-4 w-4 text-indigo-500" />Domains</button>
          <button onClick={() => setShowNew(true)} data-testid="kc-new-collection" className="inline-flex items-center gap-2 bg-[#171B2E] text-white text-sm rounded-lg px-4 py-2.5"><Icons.Plus className="h-4 w-4 text-[#F5A623]" />New Collection</button>
        </div>
      </div>

      {showDomains && <DomainManager onClose={() => setShowDomains(false)} onChanged={() => { loadMeta(); load(); }} />}
      {showTest && <TestRetrieval agents={agents} collections={colOpts} onClose={() => setShowTest(false)} />}
      {showGaps && <GapLog onClose={() => setShowGaps(false)} />}
      {showEnrich && <KnowledgeMetadataReview collectionId={enrichColId} onClose={() => { setShowEnrich(false); setEnrichColId(null); }} onChanged={() => { loadPending(); load(); loadMeta(); }} />}

      <div className="bg-indigo-50/60 border border-indigo-100 rounded-xl p-3.5 text-sm text-indigo-800 flex items-start gap-2">
        <Icons.Info className="h-4 w-4 mt-0.5 shrink-0" />
        <span>Knowledge is centralized — agents don't own it. Grant a collection to one or more agents; matching content is automatically retrieved (RAG) to ground their answers.</span>
      </div>

      {showNew && <CollectionEditor agents={agents} domains={domains} taxonomy={taxonomy} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); loadMeta(); }} />}

      {collections === null ? <div className="py-12 text-center"><Icons.Loader2 className="h-6 w-6 animate-spin text-indigo-500 inline" /></div> :
      (() => {
        const sc = search.trim().toLowerCase();
        const match = (c) => !sc || (c.name || "").toLowerCase().includes(sc) || (c.description || "").toLowerCase().includes(sc);
        const activeCols = collections.filter((c) => c.status !== "archived" && match(c));
        const archivedCols = collections.filter((c) => c.status === "archived" && match(c));

        // Build domain groups (ordered by domain sort_order), plus an Unassigned bucket.
        const domById = Object.fromEntries((domains || []).map((d) => [d.id, d]));
        const orderedDomains = [...(domains || [])].sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));
        const groups = [];
        for (const d of orderedDomains) {
          const cols = activeCols.filter((c) => c.domain_id === d.id);
          if (cols.length) groups.push({ id: d.id, name: d.name, cols });
        }
        const unassigned = activeCols.filter((c) => !c.domain_id || !domById[c.domain_id]);
        if (unassigned.length) groups.push({ id: "__unassigned", name: "Unassigned", cols: unassigned });

        const visibleGroups = domainFilter === "all" ? groups : groups.filter((g) => g.id === domainFilter);
        const domainCounts = groups.map((g) => ({ id: g.id, name: g.name, n: g.cols.length }));

        return (
          <>
            {/* ---- filter toolbar: search + domain pills ---- */}
            <div className="space-y-3" data-testid="kc-nav-toolbar">
              <div className="relative max-w-sm">
                <Icons.Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid="kc-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search collections…"
                  className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500" />
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => setDomainFilter("all")} data-testid="kc-domain-pill-all"
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${domainFilter === "all" ? "bg-[#171B2E] text-white border-[#171B2E]" : "bg-white text-slate-600 border-slate-300 hover:border-indigo-400"}`}>
                  All <span className="opacity-60">· {activeCols.length}</span>
                </button>
                {domainCounts.map((d) => (
                  <button key={d.id} onClick={() => setDomainFilter(d.id)} data-testid={`kc-domain-pill-${d.id}`}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${domainFilter === d.id ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-300 hover:border-indigo-400"}`}>
                    {d.name} <span className="opacity-60">· {d.n}</span>
                  </button>
                ))}
              </div>
            </div>

            {activeCols.length === 0 ? <p className="text-sm text-slate-400 pt-2">{sc ? "No collections match your search." : "No collections yet. Create one and add websites, files, Q&A or pasted chats."}</p> :
              visibleGroups.map((g) => {
                const isCollapsed = collapsed.has(g.id) && !sc;  // search auto-expands
                return (
                  <div key={g.id} className="space-y-3" data-testid={`kc-group-${g.id}`}>
                    <button onClick={() => toggleGroup(g.id)} data-testid={`kc-group-toggle-${g.id}`}
                      className="w-full flex items-center gap-2 text-left pt-1">
                      <Icons.ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                      <span className="text-sm font-semibold text-slate-700">{g.name}</span>
                      <span className="text-xs text-slate-400">· {g.cols.length}</span>
                      <div className="flex-1 h-px bg-slate-200 ml-2" />
                    </button>
                    {!isCollapsed && (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {g.cols.map((c) => <CollectionCard key={c.id} c={c} onOpen={() => setActive(c.id)} onEnrich={() => openEnrich(c.id)} />)}
                      </div>
                    )}
                  </div>
                );
              })}

            {archivedCols.length > 0 && (
              <div className="pt-2" data-testid="kc-archive-folder">
                <button onClick={() => setShowArchive((s) => !s)} data-testid="kc-archive-toggle"
                  className="w-full flex items-center gap-2 text-left px-3.5 py-3 rounded-xl border border-slate-200 bg-slate-100/70 hover:bg-slate-100 transition-colors">
                  <Icons.Archive className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Archive</span>
                  <span className="text-xs text-slate-400">· {archivedCols.length} collection{archivedCols.length > 1 ? "s" : ""} · not accessible by any AI</span>
                  <Icons.ChevronDown className={`h-4 w-4 text-slate-400 ml-auto transition-transform ${showArchive ? "rotate-180" : ""}`} />
                </button>
                {showArchive && (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-3">
                    {archivedCols.map((c) => <CollectionCard key={c.id} c={c} onOpen={() => setActive(c.id)} />)}
                  </div>
                )}
              </div>
            )}
          </>
        );
      })()}

      {active && <CollectionDetail cid={active} agents={agents} domains={domains} taxonomy={taxonomy} onClose={() => setActive(null)} onChanged={() => { load(); loadMeta(); }} />}
    </div>
  );
}

function CollectionCard({ c, onOpen, onEnrich }) {
  const archived = c.status === "archived";
  return (
    <div className="relative">
    <button onClick={onOpen} data-testid={`kc-collection-${c.id}`}
      className={`w-full text-left bg-white rounded-xl border shadow-sm p-4 hover:shadow-md transition-shadow ${archived ? "opacity-70 border-slate-200 bg-slate-50" : "border-slate-200"}`}>
      <div className="flex items-start justify-between">
        <div className="font-heading font-semibold text-slate-800">{c.name}</div>
        <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">P{c.priority}</span>
      </div>
      {c.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.description}</p>}
      <div className="flex items-center gap-2 mt-3 text-xs text-slate-400">
        <span className="inline-flex items-center gap-1"><Icons.FileStack className="h-3.5 w-3.5" />{c.item_count || 0} items</span>
        {c.retrieval_keywords?.length > 0 && <span className="inline-flex items-center gap-1 text-indigo-500"><Icons.Sparkles className="h-3.5 w-3.5" />{c.retrieval_keywords.length} keyword{c.retrieval_keywords.length > 1 ? "s" : ""}</span>}
        {c.ai_metadata_enrichment_enabled && <span className="inline-flex items-center gap-1 text-emerald-600"><Icons.Wand2 className="h-3.5 w-3.5" />Enrichment on</span>}
        {archived && <span className="inline-flex items-center gap-1 text-slate-500"><Icons.Archive className="h-3 w-3" />Archived · hidden from AI</span>}
      </div>
      {!archived && (
        <div className="flex items-center gap-1 mt-3 flex-wrap">
          {(c.agent_access || []).length === 0 ? <span className="text-[11px] text-slate-400">No agents granted</span> :
            (c.agent_access || []).map((k) => <span key={k} className="text-[11px] bg-violet-50 text-violet-700 rounded-full px-2 py-0.5 capitalize">{k}</span>)}
        </div>
      )}
    </button>
      {!archived && onEnrich && (
        <button onClick={(e) => { e.stopPropagation(); onEnrich(); }} data-testid={`kc-collection-enrich-${c.id}`}
          title="AI Metadata Enrichment"
          className="absolute bottom-3 right-3 p-1.5 rounded-lg text-indigo-500 hover:bg-indigo-50 transition-colors">
          <Icons.Sparkles className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function ItemAccess({ item, agents, collectionAgents, onChanged }) {
  const explicit = Array.isArray(item.agent_access);
  const value = explicit ? item.agent_access : (collectionAgents || []);
  const [busy, setBusy] = useState(false);
  const toggle = async (k) => {
    const next = value.includes(k) ? value.filter((x) => x !== k) : [...value, k];
    setBusy(true);
    try { await api.patch(`/knowledge/items/${item.id}`, { agent_access: next }); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update access"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-testid={`kc-item-access-${item.id}`}>
      <span className="text-[10px] text-slate-400 uppercase tracking-wide font-medium">Access</span>
      {agents.map((a) => {
        const on = value.includes(a.key);
        return (
          <button key={a.key} type="button" disabled={busy} onClick={() => toggle(a.key)}
            data-testid={`kc-item-access-${item.id}-${a.key}`}
            className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors disabled:opacity-50 ${on ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-500 border-slate-300 hover:border-violet-400"}`}>
            {a.name}
          </button>
        );
      })}
      {value.length === 0 && <span className="text-[10px] text-rose-400 italic">no agents — hidden from all</span>}
    </div>
  );
}

function AgentGrant({ agents, value, onChange }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div className="flex flex-wrap gap-2" data-testid="kc-agent-grant">
      {agents.map((a) => (
        <button key={a.key} type="button" onClick={() => toggle(a.key)} data-testid={`kc-grant-${a.key}`}
          className={`text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${value.includes(a.key) ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-300 hover:border-violet-400"}`}>
          {a.name}
        </button>
      ))}
    </div>
  );
}

function CollectionEditor({ agents, domains = [], taxonomy, existing, onClose, onSaved }) {
  const [f, setF] = useState(existing || { name: "", description: "", tags: [], retrieval_keywords: [], priority: 3, agent_access: [], status: "active", domain_id: null, default_jurisdiction: null, default_document_type: null, default_authority_level: null, default_agent_access: [] });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const splitList = (v) => (typeof v === "string" ? v.split(",").map((t) => t.trim()).filter(Boolean) : (v || []));
  const save = async () => {
    if (!f.name.trim()) return;
    setSaving(true);
    try {
      const payload = { ...f, tags: splitList(f.tags), retrieval_keywords: splitList(f.retrieval_keywords) };
      if (existing) await api.put(`/knowledge/collections/${existing.id}`, payload);
      else await api.post("/knowledge/collections", payload);
      toast.success("Collection saved"); onSaved();
    } catch (e) {
      const msg = e.response?.data?.detail || (e.response ? `Error ${e.response.status}` : e.message || "Network error");
      toast.error(`Could not save collection: ${msg}`);
    } finally { setSaving(false); }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3" data-testid="kc-collection-editor">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2"><label className={lbl}>Name</label><input className={inp} data-testid="kc-coll-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. FBAR & Foreign Accounts" /></div>
        <div><label className={lbl}>Priority (1–5)</label><input type="number" min="1" max="5" className={inp} value={f.priority} onChange={(e) => set("priority", Math.max(1, Math.min(5, Number(e.target.value) || 1)))} /></div>
      </div>
      <div>
        <label className={lbl}>Knowledge Domain</label>
        <select className={inp} data-testid="kc-coll-domain" value={f.domain_id || ""} onChange={(e) => set("domain_id", e.target.value || null)}>
          <option value="">— unassigned —</option>
          {domains.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>
      <div><label className={lbl}>Description</label><input className={inp} value={f.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
      <div><label className={lbl}>Tags (comma-separated)</label><input className={inp} value={Array.isArray(f.tags) ? f.tags.join(", ") : f.tags} onChange={(e) => set("tags", e.target.value)} /></div>
      <div>
        <label className={lbl}><span className="inline-flex items-center gap-1"><Icons.Sparkles className="h-3 w-3 text-indigo-500" />AI Retrieval Keywords (comma-separated)</span></label>
        <input className={inp} data-testid="kc-coll-keywords"
          value={Array.isArray(f.retrieval_keywords) ? f.retrieval_keywords.join(", ") : (f.retrieval_keywords || "")}
          onChange={(e) => set("retrieval_keywords", e.target.value)}
          placeholder="e.g. FBAR, FinCEN 114, foreign bank account, offshore accounts" />
        <p className="text-[11px] text-slate-400 mt-1">Synonyms &amp; alternate phrasings that should surface this collection during AI retrieval (RAG). Improves recall when questions use different wording than the source text.</p>
      </div>
      {taxonomy && (
        <div className="border-t border-slate-100 pt-3">
          <p className="text-[11px] text-slate-400 mb-2">Document defaults — inherited by new documents in this collection (each document can override).</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className={lbl}>Default document type</label>
              <select className={inp} data-testid="kc-coll-default-type" value={f.default_document_type || ""} onChange={(e) => set("default_document_type", e.target.value || null)}>
                <option value="">— none —</option>{taxonomy.document_types.map((o) => <option key={o} value={o}>{titleize(o)}</option>)}
              </select></div>
            <div><label className={lbl}>Default authority</label>
              <select className={inp} data-testid="kc-coll-default-authority" value={f.default_authority_level || ""} onChange={(e) => set("default_authority_level", e.target.value || null)}>
                <option value="">— none —</option>{taxonomy.authority_levels.map((o) => <option key={o} value={o}>{titleize(o)}</option>)}
              </select></div>
            <div><label className={lbl}>Default jurisdiction</label>
              <select className={inp} data-testid="kc-coll-default-jurisdiction" value={f.default_jurisdiction || ""} onChange={(e) => set("default_jurisdiction", e.target.value || null)}>
                <option value="">— none —</option>{taxonomy.jurisdictions.map((o) => <option key={o} value={o}>{titleize(o)}</option>)}
              </select></div>
          </div>
        </div>
      )}
      <div><label className={lbl}>Agent access</label><AgentGrant agents={agents} value={f.agent_access} onChange={(v) => set("agent_access", v)} /></div>
      {existing && <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.status === "active"} onChange={(e) => set("status", e.target.checked ? "active" : "archived")} className="accent-indigo-600" />Active</label>}
      <div className="flex gap-2"><button onClick={save} disabled={saving || !f.name.trim()} data-testid="kc-coll-save" className="bg-[#1f6feb] text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50">Save</button><button onClick={onClose} className="text-sm text-slate-500 px-3">Cancel</button></div>
    </div>
  );
}

function CollectionDetail({ cid, agents, domains, taxonomy, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("website");
  const [editing, setEditing] = useState(false);
  const [docEdit, setDocEdit] = useState(null);
  const load = useCallback(() => api.get(`/knowledge/collections/${cid}`).then((r) => setData(r.data)).catch(() => {}), [cid]);
  useEffect(() => { load(); }, [load]);

  const archive = async () => { try { await api.delete(`/knowledge/collections/${cid}`); toast.success("Archived"); onChanged(); onClose(); } catch { toast.error("Failed"); } };
  const delItem = async (id) => { try { await api.delete(`/knowledge/items/${id}`); toast.success("Removed"); load(); onChanged(); } catch { toast.error("Failed"); } };

  const c = data?.collection;
  const domName = c && domains?.find((d) => d.id === c.domain_id)?.name;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose} data-testid="kc-detail-overlay">
      <div className="w-full max-w-2xl bg-slate-50 h-full overflow-y-auto tf-scroll" onClick={(e) => e.stopPropagation()} data-testid="kc-detail-panel">
        {!data ? <div className="p-10 text-center"><Icons.Loader2 className="h-6 w-6 animate-spin text-indigo-500 inline" /></div> : (
          <>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between">
              <div>
                <h2 className="font-heading text-lg font-semibold text-slate-900">{c.name}</h2>
                <p className="text-xs text-slate-500 mt-0.5">{domName ? `${domName} · ` : ""}Priority {c.priority} · {(c.agent_access || []).join(", ") || "no agents"} · {data.items.length} items</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setEditing((e) => !e)} data-testid="kc-edit-collection" className="p-2 hover:bg-slate-100 rounded text-slate-500"><Icons.Settings2 className="h-4 w-4" /></button>
                <button onClick={archive} data-testid="kc-archive-collection" className="p-2 hover:bg-slate-100 rounded text-rose-400"><Icons.Archive className="h-4 w-4" /></button>
                <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded text-slate-500"><Icons.X className="h-4 w-4" /></button>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {editing && <CollectionEditor agents={agents} domains={domains} taxonomy={taxonomy} existing={{ ...c, tags: c.tags || [], retrieval_keywords: c.retrieval_keywords || [] }} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); onChanged(); }} />}

              {docEdit && taxonomy && <DocumentEditor itemId={docEdit} collection={c} agents={agents} taxonomy={taxonomy} onClose={() => setDocEdit(null)} onChanged={() => { load(); onChanged(); }} />}

              {(c.description || c.tags?.length > 0 || c.retrieval_keywords?.length > 0) && (
                <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2.5" data-testid="kc-collection-meta">
                  {c.description && <p className="text-sm text-slate-600">{c.description}</p>}
                  {c.tags?.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Tags</span>
                      {c.tags.map((t) => <span key={t} className="text-[11px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{t}</span>)}
                    </div>
                  )}
                  {c.retrieval_keywords?.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap" data-testid="kc-retrieval-keywords">
                      <span className="text-[10px] uppercase tracking-wide text-indigo-400 font-medium inline-flex items-center gap-1"><Icons.Sparkles className="h-3 w-3" />AI Retrieval Keywords</span>
                      {c.retrieval_keywords.map((k) => <span key={k} className="text-[11px] bg-indigo-50 text-indigo-700 rounded-full px-2 py-0.5">{k}</span>)}
                    </div>
                  )}
                </div>
              )}

              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex gap-1.5 flex-wrap border-b border-slate-100 mb-3">
                  {Object.entries(SOURCE_META).map(([k, m]) => {
                    const Ic = Icons[m.icon] || Icons.File;
                    return <button key={k} onClick={() => setTab(k)} data-testid={`kc-addtab-${k}`}
                      className={`inline-flex items-center gap-1.5 text-sm px-3 py-2 border-b-2 -mb-px ${tab === k ? "border-indigo-600 text-indigo-700 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"}`}><Ic className="h-4 w-4" />{m.label}</button>;
                  })}
                </div>
                <AddSource cid={cid} type={tab} onAdded={() => { load(); onChanged(); }} />
              </div>

              <div className="space-y-2" data-testid="kc-items">
                {data.items.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">No items yet. Add a source above.</p> :
                  data.items.map((it) => {
                    const m = SOURCE_META[it.source_type] || { label: it.source_type, icon: "File", tone: "text-slate-500 bg-slate-50" };
                    const Ic = Icons[m.icon] || Icons.File;
                    return (
                      <div key={it.id} className="bg-white rounded-lg border border-slate-200 p-3 flex items-start justify-between gap-3" data-testid={`kc-item-${it.id}`}>
                        <div className="flex items-start gap-2.5 min-w-0">
                          <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${m.tone}`}><Ic className="h-4 w-4" /></span>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-800 truncate">{it.title}</div>
                            <div className="text-xs text-slate-400">{m.label} · {it.char_count?.toLocaleString() || 0} chars
                              {it.status === "error" && <span className="text-rose-500"> · failed: {it.error?.slice(0, 50)}</span>}
                              {it.url && <span> · <a href={it.url} target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline">link</a></span>}</div>
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              {it.document_type && <span className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{titleize(it.document_type)}</span>}
                              {it.status && it.status !== "current" && it.status !== "error" && <span className={`text-[10px] rounded px-1.5 py-0.5 ${it.status === "superseded" ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-700"}`}>{titleize(it.status)}</span>}
                              {it.tax_year && <span className="text-[10px] bg-blue-50 text-blue-600 rounded px-1.5 py-0.5">TY {it.tax_year}</span>}
                              {it.allow_penny && <span className="text-[10px] bg-violet-50 text-violet-600 rounded px-1.5 py-0.5">Penny OK</span>}
                              {typeof it.section_count === "number" && <span className="text-[10px] text-slate-400">{it.section_count} sections{it.section_fallback ? " (fallback)" : ""}</span>}
                            </div>
                            <ItemAccess item={it} agents={agents} collectionAgents={c.agent_access} onChanged={() => { load(); onChanged(); }} />
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => setDocEdit(it.id)} data-testid={`kc-item-meta-${it.id}`} className="p-1.5 hover:bg-slate-100 rounded text-slate-500" title="Metadata & sections"><Icons.SlidersHorizontal className="h-3.5 w-3.5" /></button>
                          <button onClick={() => delItem(it.id)} data-testid={`kc-delitem-${it.id}`} className="p-1.5 hover:bg-rose-50 rounded text-rose-400" title="Remove"><Icons.Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AddSource({ cid, type, onAdded }) {
  const [busy, setBusy] = useState(false);
  const [v, setV] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const [queue, setQueue] = useState([]);
  const fileRef = useRef();
  const reset = () => setV({});

  const submit = async (fn) => { setBusy(true); try { await fn(); toast.success("Added to collection"); reset(); onAdded(); } catch (e) { const msg = e.response?.data?.detail || (e.response ? `Error ${e.response.status}` : e.message || "Network error"); toast.error(`Upload failed: ${msg}`); } finally { setBusy(false); } };

  if (type === "qa") {
    return (
      <div className="space-y-2">
        <input className={inp} placeholder="Question" data-testid="kc-qa-question" value={v.question || ""} onChange={(e) => setV({ ...v, question: e.target.value })} />
        <textarea className={inp} rows={3} placeholder="Answer" data-testid="kc-qa-answer" value={v.answer || ""} onChange={(e) => setV({ ...v, answer: e.target.value })} />
        <button disabled={busy || !v.question || !v.answer} data-testid="kc-add-qa" onClick={() => submit(() => api.post(`/knowledge/collections/${cid}/items/qa`, { question: v.question, answer: v.answer }))} className="bg-indigo-600 text-white rounded-lg px-3.5 py-2 text-sm disabled:opacity-50">Add Q&A</button>
      </div>
    );
  }
  if (type === "chat") {
    return (
      <div className="space-y-2">
        <input className={inp} placeholder="Title (e.g. 'Streamlined filing chat from prior project')" data-testid="kc-chat-title" value={v.title || ""} onChange={(e) => setV({ ...v, title: e.target.value })} />
        <textarea className={inp} rows={5} placeholder="Paste the conversation here…" data-testid="kc-chat-content" value={v.content || ""} onChange={(e) => setV({ ...v, content: e.target.value })} />
        <button disabled={busy || !v.title || !v.content} data-testid="kc-add-chat" onClick={() => submit(() => api.post(`/knowledge/collections/${cid}/items/chat`, { title: v.title, content: v.content }))} className="bg-indigo-600 text-white rounded-lg px-3.5 py-2 text-sm disabled:opacity-50">Add Chat</button>
      </div>
    );
  }
  if (type === "website") {
    return (
      <div className="space-y-2">
        <input className={inp} placeholder="https://…" data-testid="kc-web-url" value={v.url || ""} onChange={(e) => setV({ ...v, url: e.target.value })} />
        <p className="text-xs text-slate-400">Single-page extraction. Recursive crawling & scheduled refresh coming later.</p>
        <button disabled={busy || !v.url} data-testid="kc-add-website" onClick={() => submit(() => api.post(`/knowledge/collections/${cid}/items/website`, { url: v.url }))} className="bg-indigo-600 text-white rounded-lg px-3.5 py-2 text-sm disabled:opacity-50 inline-flex items-center gap-2">{busy && <Icons.Loader2 className="h-4 w-4 animate-spin" />}Fetch & Index</button>
      </div>
    );
  }
  // file uploads (pdf / word / spreadsheet) — multi-file drag & drop
  const accept = type === "pdf" ? ".pdf" : type === "word" ? ".doc,.docx" : ".xlsx,.xls,.csv";
  const label = type === "pdf" ? "PDF" : type === "word" ? "Word" : "spreadsheet";
  const uploadOne = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    await api.post(`/knowledge/collections/${cid}/items/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  };
  const uploadFiles = async (fileList) => {
    const list = Array.from(fileList);
    if (!list.length) return;
    setQueue(list.map((f) => ({ name: f.name, status: "pending" })));
    setBusy(true);
    let ok = 0, fail = 0;
    for (let i = 0; i < list.length; i++) {
      setQueue((q) => q.map((it, idx) => (idx === i ? { ...it, status: "uploading" } : it)));
      try {
        await uploadOne(list[i]);
        ok++;
        setQueue((q) => q.map((it, idx) => (idx === i ? { ...it, status: "done" } : it)));
      } catch (e) {
        fail++;
        const msg = e.response?.data?.detail || (e.response ? `Error ${e.response.status}` : e.message || "Network error");
        setQueue((q) => q.map((it, idx) => (idx === i ? { ...it, status: "error", error: msg } : it)));
      }
    }
    setBusy(false);
    if (ok) toast.success(`Added ${ok} document${ok > 1 ? "s" : ""} to collection`);
    if (fail) toast.error(`${fail} file${fail > 1 ? "s" : ""} failed to upload`);
    onAdded();
  };
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  };
  return (
    <div className="space-y-2">
      <input ref={fileRef} type="file" accept={accept} multiple className="hidden" data-testid={`kc-file-${type}`}
        onChange={(e) => { if (e.target.files?.length) uploadFiles(e.target.files); e.target.value = ""; }} />
      <div data-testid={`kc-dropzone-${type}`}
        onClick={() => { if (!busy) fileRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
        onDrop={onDrop}
        className={`w-full border-2 border-dashed rounded-lg py-6 px-4 text-sm inline-flex flex-col items-center gap-1.5 cursor-pointer transition-colors ${dragOver ? "border-indigo-500 bg-indigo-50 text-indigo-600" : "border-slate-300 text-slate-500 hover:border-indigo-400 hover:text-indigo-600"} ${busy ? "opacity-60 pointer-events-none" : ""}`}>
        {busy ? <Icons.Loader2 className="h-5 w-5 animate-spin" /> : <Icons.Upload className="h-5 w-5" />}
        <span className="font-medium">{dragOver ? `Drop ${label} files here` : `Drag & drop ${label} files here`}</span>
        <span className="text-xs text-slate-400">or click to browse ({accept}) — multiple files supported</span>
      </div>
      {queue.length > 0 && (
        <div className="space-y-1" data-testid={`kc-upload-queue-${type}`}>
          {queue.map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-slate-600 bg-slate-50 rounded px-2 py-1">
              {f.status === "uploading" && <Icons.Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-500 shrink-0" />}
              {f.status === "done" && <Icons.CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
              {f.status === "error" && <Icons.AlertCircle className="h-3.5 w-3.5 text-rose-500 shrink-0" />}
              {f.status === "pending" && <Icons.Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
              <span className="truncate flex-1">{f.name}</span>
              {f.status === "error" && <span className="text-rose-500 shrink-0 max-w-[40%] truncate" title={f.error}>{f.error}</span>}
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400">The original file is stored and its text is extracted for retrieval.</p>
    </div>
  );
}
